import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import elementType from 'prop-types-extra/lib/elementType';
import withRestProps from 'helpers/withRestProps';
import { noop } from 'lodash/fp';
import cns from 'classnames';
import { useIsKeyboardNavigationState } from 'components/KeyboardNavigationContext';

import CheckboxIcon from 'components/icons/CheckboxIcon.com.svg';
import CheckboxOutlineBlankIcon from 'components/icons/CheckboxOutlineBlankIcon.com.svg';
import RadioboxMarkedIcon from 'components/icons/RadioboxMarkedIcon.com.svg';
import RadioboxBlankIcon from 'components/icons/RadioboxBlankIcon.com.svg';
import cn from './styles.local.less';

const propTypes = {
  input: PropTypes.object.isRequired,
  className: PropTypes.string,
  boxClassName: PropTypes.string,
  type: PropTypes.oneOf(['radio', 'checkbox']),
  Box: elementType,
  Icon: elementType,
  Label: elementType,
  onChanged: PropTypes.func,
  rest: PropTypes.object,
  hasError: PropTypes.bool,
  isTabbing: PropTypes.bool,
};

class Control extends PureComponent {
  static propTypes = propTypes;

  static defaultProps = {
    Box: ({ className, inputNode, iconNode, labelNode, value }) => (
      <label className={className} data-qa-value={value}>
        {inputNode}
        {iconNode}
        {labelNode}
      </label>
    ),
    Icon: ({ boxElement }) => boxElement,
    Label: ({ label = null, required = false }) => (
      <span>
        {label}
        {required ? '*' : null}
      </span>
    ),
    onChanged: noop,
  };

  state = {
    hasFocus: false,
  };

  getInputProps() {
    const { input, type } = this.props;

    return {
      ...input,
      type,
      className: cn.input,
      onChange: this.handleChange,
      onFocus: (e) => {
        if (input.onFocus) input.onFocus(e);
        this.setState({ hasFocus: true });
      },
      onBlur: (e) => {
        input.onBlur(e);
        this.setState({ hasFocus: false });
      },
    };
  }

  handleChange = (event) => {
    const { input, onChanged } = this.props;

    input.onChange(event);
    onChanged(!this.props.input.checked);
  };

  renderRadiobox() {
    const { input } = this.props;

    return input.checked ? (
      <RadioboxMarkedIcon className={cn.svgIcon} />
    ) : (
      <RadioboxBlankIcon className={cn.svgIcon} />
    );
  }

  renderCheckbox() {
    const { input } = this.props;

    return input.checked ? (
      <CheckboxIcon className={cn.svgIcon} />
    ) : (
      <CheckboxOutlineBlankIcon className={cn.svgIcon} />
    );
  }

  renderBoxElement() {
    const { type, input, boxClassName } = this.props;

    return (
      <span
        className={cns(cn.box, { [cn.checked]: input.checked }, boxClassName)}
      >
        {type === 'checkbox' ? this.renderCheckbox() : null}
        {type === 'radio' ? this.renderRadiobox() : null}
      </span>
    );
  }

  render() {
    const { className, Box, input, Label, Icon, rest, hasError, isTabbing } =
      this.props;
    const { hasFocus } = this.state;

    return (
      <Box
        {...rest}
        input={input}
        className={cns(cn.root, className, {
          [cn.disabled]: input.disabled,
          [cn.hasError]: hasError,
          [cn.ringFocus]: hasFocus && isTabbing,
        })}
        inputNode={<input {...this.getInputProps()} />}
        iconNode={<Icon {...this.props} boxElement={this.renderBoxElement()} />}
        labelNode={<Label {...this.props} />}
      />
    );
  }
}

function withKeyboardNavigation(Comp) {
  return function Inner(props) {
    const isTabbing = useIsKeyboardNavigationState();

    return <Comp {...props} isTabbing={isTabbing} />;
  };
}

export default withKeyboardNavigation(withRestProps()(Control));
